import { numberText } from "./intl";
import { t } from "./t";
import type { PlainKey } from "./types";

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * G-85 S5 — the server's refusals, in the florist's language (OD-64)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `apiClient.buildError` puts the server's English `message` straight onto
 * `Error.message`, and 57 places in the kiosk render it. So a Russian florist
 * gets a fully Russian screen, taps Receive, and reads an English sentence —
 * on the FAILURE path, which is exactly when a person most needs to
 * understand what happened and what to do next.
 *
 * Owner, 2026-09-21, asked directly: *"yes Kiosk error messages are in the
 * scope."*
 *
 * ── Why a map on the CLIENT and not translation on the server ──────────────
 *
 * The server does not know who is reading. The floor token names a person,
 * but the language is a UI choice that can be changed on the lock screen for
 * one shift without touching the account. Translating server-side would mean
 * threading a locale through every controller and service to produce a string
 * the client then cannot re-render when the language changes mid-shift.
 *
 * ── The cost of a map, stated plainly ──────────────────────────────────────
 *
 * It matches on ENGLISH SENTENCES. Reword an exception and the map silently
 * stops matching, and the florist is back to reading English. That is what
 * `serverErrors.spec` is for: it walks `src/**` and fails when a mapped
 * sentence no longer appears in a throw. The map cannot rot quietly.
 *
 * ── Several English sentences, one key ─────────────────────────────────────
 *
 * Deliberate. "Missing floor token", "Invalid or expired floor token",
 * "Session reference is invalid" and six others are one thing to the person
 * holding the tablet: the shift session is over, PIN in again. The server
 * distinguishes them because a developer reading a log needs to; the florist
 * needs to know which button to press.
 *
 * ── Developer strings become human ones ────────────────────────────────────
 *
 * "Missing X-Shop-Floor-Session-Id header" is not translated into Russian as
 * a header name. It maps to the sentence a florist can act on. Same for
 * "Token type not accepted on floor route" and "locationId is required for
 * Owner/Admin".
 *
 * ── What is NOT mapped ─────────────────────────────────────────────────────
 *
 * Messages thrown by the services the kiosk's controllers delegate to —
 * inventory, sales, bouquets — are outside this pass. `errorText` returns
 * them verbatim, which is English but TRUE. A generic "something went wrong"
 * would be worse: it throws away the one sentence that says what happened.
 */

/**
 * The server's English → the key the florist reads.
 *
 * Grouped by what the person must DO about it, which is why the groups are
 * uneven: nine session messages collapse to one sentence, and "Nothing to
 * change" stands alone.
 */
const SERVER_MESSAGES: Readonly<Record<string, PlainKey>> = {
  // ── the shift session is over; PIN in again ──────────────────────────────
  "Floor session is inactive or expired": "serverError.sessionEnded",
  "Shop floor session is invalid or expired": "serverError.sessionEnded",
  "Shop floor session not found": "serverError.sessionEnded",
  "Shop floor session invalid or inactive location": "serverError.sessionEnded",
  "Invalid or expired floor token": "serverError.sessionEnded",
  "Missing floor token": "serverError.sessionEnded",
  "Session reference is invalid": "serverError.sessionEnded",
  "Floor session mismatch": "serverError.sessionEnded",
  // Developer-facing on the wire; a florist needs the sentence above.
  "Missing X-Shop-Floor-Session-Id header": "serverError.sessionEnded",
  "Token type not accepted on floor route": "serverError.sessionEnded",

  // ── someone else set this tablet up ──────────────────────────────────────
  "You did not arm this session": "serverError.notYourSession",
  "Session was not armed by the authenticated user": "serverError.notYourSession",

  // ── signing in ───────────────────────────────────────────────────────────
  "Invalid PIN": "serverError.pinInvalid",
  "That account is not active": "serverError.accountInactive",
  "This fob is not recognised": "serverError.fobUnknown",
  "Couriers do not use the shop floor kiosk": "serverError.couriersNoKiosk",
  "Couriers do not use shop-floor PINs": "serverError.couriersNoKiosk",
  "Authentication required to arm or exit shop mode": "serverError.signInToArm",
  "This role cannot arm shop mode": "serverError.roleCannotArm",
  "Could not allocate a unique PIN — retry": "serverError.pinAllocationFailed",

  // ── not your job, or not your shop ───────────────────────────────────────
  "Insufficient permissions": "serverError.notAllowed",
  "Retiring a box is a manager's job": "serverError.managerOnlyRetireBox",
  "Only an owner or admin can issue a fob": "serverError.ownerAdminOnlyFob",
  "Only an owner or admin can revoke a fob": "serverError.ownerAdminOnlyFob",
  "Only owners, admins, and managers can manage recurring tasks":
    "serverError.managerOnlyRecurringTasks",
  // G-103: the self-only PIN rule now covers housekeeping too, so the server
  // stopped naming florists in it. The key keeps its name; the five
  // translations already said "your own PIN".
  "You can only change your own PIN": "serverError.floristsOwnPinOnly",
  // G-103 S1: a cleaner's correct PIN/fob at the kiosk is refused until the
  // presence door lands in S3 — the one refusal a cleaner will actually read.
  "Housekeeping presence is not available on this tablet yet":
    "serverError.housekeepingNoKiosk",
  "Managers can only change florist PINs": "serverError.managersFloristPinsOnly",
  "Managers must be assigned to a shop": "serverError.managerNeedsShop",
  "Target florist is not assigned to your shop": "serverError.notYourShopFlorist",
  "You have no active location assignment": "serverError.noLocationAssignment",
  "locationId is required for Owner/Admin": "serverError.pickShopFirst",

  // ── it is not there any more ─────────────────────────────────────────────
  "Item not found": "serverError.itemGone",
  "Box not found": "serverError.boxGone",
  "No box with this code in this shop": "serverError.boxGone",
  "User not found": "serverError.userGone",
  "Floor worker not found": "serverError.userGone",
  "Location not found": "serverError.locationGone",
  "Fob not found": "serverError.fobGone",
  "Log entry not found": "serverError.logEntryGone",
  "No such photo search": "serverError.photoSearchGone",

  // ── photographs ──────────────────────────────────────────────────────────
  "Attach a photo file": "serverError.attachPhoto",
  "No photo received": "serverError.attachPhoto",
  "Photo must be JPEG, PNG, WEBP, or HEIC": "serverError.photoFormat",
  "That file is not a readable photo": "serverError.photoUnreadable",
  "Invalid photo path": "serverError.photoUnreadable",
  "This photo search was already answered": "serverError.photoSearchAnswered",

  // ── undoing something ────────────────────────────────────────────────────
  "This action was already undone": "serverError.alreadyUndone",
  "This kind of action cannot be undone from the log": "serverError.cannotUndoFromLog",
  "Say why this is being undone": "serverError.sayWhyUndo",
  "Some of this stock was already sold or used — it cannot be undone":
    "serverError.stockAlreadyUsed",
  "A source batch no longer exists — this cannot be reversed":
    "serverError.sourceBatchGone",
  "This receive row predates the metadata the undo needs": "serverError.rowPredatesUndo",
  "This waste row predates the metadata the undo needs": "serverError.rowPredatesUndo",
  "The batch no longer exists": "serverError.batchGone",
  "The batch id is missing from this row": "serverError.detailMissingForUndo",
  "The sale id is missing": "serverError.detailMissingForUndo",
  "The build id is missing": "serverError.detailMissingForUndo",
  "This sale no longer exists": "serverError.saleGone",
  "This build no longer exists": "serverError.buildGone",
  "This build was already voided": "serverError.buildVoided",
  "Undo its sale first — the bouquet is sold": "serverError.undoSaleFirst",
  // G-151: an undone sale is kept (VOIDED) — no ticket, no below-floor reason.
  "This sale was undone — there is no ticket to print": "serverError.saleUndoneNoTicket",
  "This sale was undone — it needs no reason": "serverError.saleUndoneNoReason",
  // G-162: a refund says the sale happened — the Action log cannot undo it.
  "This sale was refunded — it cannot be undone": "serverError.saleRefundedNoUndo",

  // ── correct it somewhere else ────────────────────────────────────────────
  "This bouquet belongs to an order — correct it through the order":
    "serverError.correctThroughOrder",
  "This sale completed an order — correct it through the order, not here":
    "serverError.correctThroughOrder",
  "The courier already has this delivery — correct it through the delivery job, not here":
    "serverError.courierHasDelivery",
  "A courier order needs the delivery details": "serverError.courierOrderNeedsDetails",
  // G-160 (OD-265): a stale card accepting an order an owner skipped meanwhile.
  "This web order was taken out of the queue": "serverError.webOrderSkipped",
  // KI-75 / G-167: the web-order panel's Accept and Hand to delivery refusals.
  "Order already taken": "serverError.webOrderTaken",
  "This web order is due later than tomorrow — the office assigns it":
    "serverError.webOrderOfficeAssigns",
  "This shop can no longer fulfil the whole order": "serverError.webOrderNoLongerFits",
  "This shop is not the mapped pickup shop": "serverError.webOrderOtherPickupShop",
  "Build every bouquet before hand-off": "serverError.webOrderBuildFirst",
  "Order has an unlinked line": "serverError.webOrderUnlinkedLine",
  "Order is not claimed by this shop": "serverError.webOrderNotHere",

  // ── receiving a delivery (G-121 S2) ──────────────────────────────────────
  "Damaged quantity cannot exceed the received quantity":
    "serverError.damagedExceedsReceived",
  "A missing line cannot have damaged units": "serverError.missingLineNoDamage",
  "A photo of the damaged items is required": "serverError.damagePhotoRequired",
  // Names the API flag; the florist needs the two buttons it stands for.
  "Damaged units are reported with the received quantity, not with asExpected":
    "serverError.damageNeedsReceivedQty",
  "The same item appears twice for one zone; merge them into one line":
    "serverError.duplicateItemZone",

  // ── typed purchase money (G-121 S3) ──────────────────────────────────────
  "Say whether the price is before IVA or includes IVA": "serverError.ivaBasisRequired",
  // G-121 S4 (OD-150): a florist sent a price from the kiosk receive.
  "Only a manager can enter a purchase price on the shop floor": "serverError.managerOnlyReceivePrice",
  // G-174 (KI-100): the supplier on a priced receive.
  "A supplier is recorded only with a price.": "serverError.supplierNeedsPrice",
  "Supplier not found or inactive": "serverError.supplierInactive",

  // ── how the customer paid (G-141, OD-215) ────────────────────────────────
  // The till checks these first; the server's copy answers a stale screen.
  "Pay with one method, or split between cash and card": "serverError.paymentSplitCount",
  "Each payment method can be used once": "serverError.paymentSplitSameMethod",
  "Type the amount of each part of a split payment": "serverError.paymentSplitPartAmount",
  "Each part of a split payment must be more than zero": "serverError.paymentSplitPartPositive",
  "Cash and card must add up to the total": "serverError.paymentSplitSum",
  "The amount paid must be the total": "serverError.paymentSplitSingle",

  // ── a web order leaves through "Hand to delivery" only (G-166, KI-147) ───
  'This is a web order — hand it over with "Hand to delivery" on the web orders list first':
    "serverError.webOrderHandToDelivery",
  // KI-173: a web order Shopify cancelled never leaves the shop.
  "This web order was cancelled in Shopify — it must not leave the shop":
    "serverError.webOrderCancelledInShopify",
  // G-176: a held web order Shopify cancelled — its bouquet goes to the shelf.
  "This web order was cancelled in Shopify — a bouquet built for it is ordinary shelf stock now":
    "serverError.webOrderCancelledShelfBouquet",

  // ── an order's lines exceed the shop's stock (KI-9; with its list the
  //    code STOCK_SHORTAGE answers first and names each item) ───────────────
  "Location does not have enough stock": "serverError.notEnoughStock",

  // ── money recorded on a new order names its method (G-177, KI-7) ─────────
  "Say how the customer paid": "serverError.prepaidMethodNeeded",

  // ── the rest ─────────────────────────────────────────────────────────────
  "Nothing to change": "serverError.nothingToChange",
  "The start of the range is after its end": "serverError.rangeBackwards",
  "Zone is not valid for this shop": "serverError.zoneNotInShop",
};

/**
 * ── CODES beat sentences, where a code exists ──────────────────────────────
 *
 * One throw in `shop-mode` is the object form — `new ForbiddenException({
 * code, message })` — and my first scan missed the whole class, because it
 * only read throws whose first argument is a string literal. The S5 spec
 * predicted exactly this ("catches the object-form throttle too").
 *
 * It is also the better contract: `PIN_OVER_CEILING` is stable by design,
 * while the sentence beside it can be reworded any day. So a code is checked
 * FIRST, and the sentence map is the fallback rather than the rule.
 */
const SERVER_CODES: Readonly<Record<string, PlainKey>> = {
  PIN_OVER_CEILING: "serverError.PIN_OVER_CEILING",
  STOCK_SHORTAGE: "serverError.STOCK_SHORTAGE",
};

/**
 * That message names the ROLE that armed the tablet, which is the actionable
 * part — it tells the florist whose account to ask for. The role arrives only
 * inside the English sentence, so it is lifted back out and translated
 * through the `role.*` keys the hub already uses.
 */
/** Only these four exist; anything else prints the server's own word. */
const ROLE_KEYS = new Set(["role.OWNER", "role.ADMIN", "role.MANAGER", "role.FLORIST"]);

const ARMED_BY_PREFIX = "This tablet was put into kiosk mode by a ";
const ARMED_BY_SUFFIX = ".";

function armerRole(message: string | undefined): string {
  if (!message || !message.startsWith(ARMED_BY_PREFIX)) return "";
  const rest = message.slice(ARMED_BY_PREFIX.length);
  // `indexOf` returns -1 when the sentence has no period, and `slice(0, -1)`
  // then drops the LAST CHARACTER rather than returning empty — "manager"
  // became "manage" and printed that way. `dynamicMessage` below always had
  // this guard; this one did not (review of S5d).
  const end = rest.indexOf(ARMED_BY_SUFFIX);
  const word = (end < 0 ? rest : rest.slice(0, end)).trim();
  if (!word) return "";
  const key = `role.${word.toUpperCase()}`;
  // Same guard as taxonomy: only translate a role the dictionary really has,
  // or a new role would print "role.SUPERVISOR" at the florist.
  return ROLE_KEYS.has(key) ? t(key as PlainKey) : word;
}

function codeMessage(err: unknown): string | null {
  const body = (err as { body?: { code?: unknown } } | null)?.body;
  const code = typeof body?.code === "string" ? body.code : null;
  if (!code) return null;
  const key = SERVER_CODES[code];
  if (!key) return null;
  if (key === "serverError.PIN_OVER_CEILING") {
    return t(key, { role: armerRole(err instanceof Error ? err.message : undefined) });
  }
  if (key === "serverError.STOCK_SHORTAGE") {
    // No list: the sentence map answers with the plain sentence.
    const items = shortageList(body);
    return items === "" ? null : t(key, { items });
  }
  return t(key);
}

/**
 * KI-9: an order's lines exceed what the shop holds, and the server names
 * each short item under `details.shortages` — "Red Rose (8 of 12)". The
 * item name is data and passes through as the catalogue spells it.
 */
function shortageList(body: unknown): string {
  const details = (body as { details?: { shortages?: unknown } } | null)?.details;
  const rows = Array.isArray(details?.shortages) ? details.shortages : [];
  return rows
    .flatMap((row: unknown) => {
      if (!row || typeof row !== "object") return [];
      const { name, requested, available } = row as Record<string, unknown>;
      if (typeof name !== "string") return [];
      return [
        t("serverError.shortageItem", {
          name,
          available: numberText(Number(available)),
          requested: numberText(Number(requested)),
        }),
      ];
    })
    .join(", ");
}

/**
 * The one message the server builds at runtime, so equality cannot reach it:
 * `Only a manager can change ${blocked.join(", ")} from the shop floor…`.
 * The field list is data, not prose, and passes through as it arrives.
 */
const MANAGER_ONLY_PREFIX = "Only a manager can change ";
const MANAGER_ONLY_SUFFIX = " from the shop floor.";

/**
 * `apiClient.buildError` synthesises `Request failed with 502` when a response
 * carries no message of its own — a gateway error, a crashed handler, a proxy
 * timeout. It is not a sentence anyone wrote, so it is not in the map, and it
 * reached the florist verbatim in the middle of an otherwise Russian screen
 * (review of S5d). The status is kept: it is the one fact that helps whoever
 * they ask.
 */
const REQUEST_FAILED_PREFIX = "Request failed with ";

/**
 * KI-75: the two web-order refusals that name data — the shop a split order
 * is offered to (`This order is offered to ${shop}`) and the line holding
 * several bouquets (`"${title}" has quantity N — multiple bouquets…`).
 */
const OFFERED_TO_PREFIX = "This order is offered to ";
const MULTI_BOUQUET_SUFFIX =
  " — multiple bouquets per line are not supported yet; split the order in Shopify admin";

function dynamicMessage(message: string): string | null {
  if (message.startsWith(REQUEST_FAILED_PREFIX)) {
    return t("serverError.requestFailed", {
      status: message.slice(REQUEST_FAILED_PREFIX.length).trim(),
    });
  }
  if (message.startsWith(OFFERED_TO_PREFIX)) {
    return t("serverError.webOrderOfferedTo", { shop: message.slice(OFFERED_TO_PREFIX.length) });
  }
  if (message.endsWith(MULTI_BOUQUET_SUFFIX)) {
    const line = message.slice(0, -MULTI_BOUQUET_SUFFIX.length).replace(/ has quantity .*$/, "");
    return t("serverError.webOrderMultiBouquet", { line });
  }
  if (!message.startsWith(MANAGER_ONLY_PREFIX)) return null;
  const end = message.indexOf(MANAGER_ONLY_SUFFIX);
  if (end < 0) return null;
  const fields = message.slice(MANAGER_ONLY_PREFIX.length, end);
  return t("serverError.managerOnlyFields", { fields });
}

/** Exported for the spec, which checks every one still exists in `src/`. */
export function mappedServerMessages(): readonly string[] {
  return Object.keys(SERVER_MESSAGES);
}

/**
 * What to show when a call failed.
 *
 * `fallback` is used only when the failure carried no message at all — a
 * dropped connection, an aborted request. A server sentence we do not
 * recognise is returned AS IS: English, but true, and far better than a
 * generic apology that discards the only fact available.
 */
export function errorText(err: unknown, fallback: PlainKey): string {
  const byCode = codeMessage(err);
  if (byCode !== null) return byCode;
  if (err instanceof Error && err.message) {
    const key = SERVER_MESSAGES[err.message];
    if (key) return t(key);
    const dynamic = dynamicMessage(err.message);
    if (dynamic !== null) return dynamic;
    return err.message;
  }
  return t(fallback);
}
